import { PagesId } from '@ts-enums/PagesId'
import { Translations } from '@ts-types/Translations'

const globalTranslations: Translations = {
  es: {
    title: 'Productos e inventario',
    description:
      'Gestiona tus productos e inventario con guías prácticas. Controla stock, organiza catálogos, optimiza procesos y haz crecer tu negocio.',
    seo_keywords:
      'inventario, productos, wicadu, gestión de inventario, productos e inventario, software de inventario, organización de catálogo de productos, cambiar precios productos, gestión de inventario para principiantes, cómo agregar productos, cómo vender más online',
    managing_items: 'Gestión de inventario',
    customize_items: 'Personalización de productos',
    page_id: PagesId.ItemsHome,
    breadcrumbs: {
      home: 'Inicio',
      items_home: 'Productos e inventario',
    },
    faq: [
      {
        question: '¿Puedo controlar el stock de mis productos?',
        answer: 'Sí, cada producto tiene un campo de <em>inventario</em> donde puedes indicar cuántas <strong>unidades disponibles</strong> tienes, facilitando la <em>gestión de inventario</em>.'
      },
      {
        question: '¿Qué pasa cuando un producto se queda sin stock?',
        answer: 'El producto se marca automáticamente como <strong>“Agotado”</strong> y deja de estar disponible para la venta. Para reponerlo, debes agregar más unidades mediante el módulo de <em>compras</em>.'
      },
      {
        question: '¿Puedo importar muchos productos a la vez?',
        answer: 'Por ahora, wicadu solo permite personalizar <em>productos</em> individualmente con todos sus detalles, <strong>variantes</strong> e imágenes de forma rápida y sencilla.'
      },
      {
        question: '¿wicadu permite variantes como tallas o colores?',
        answer: 'Sí, puedes crear <strong>variantes</strong> como <em>talla</em>, <em>color</em> o <em>material</em> dentro del mismo producto para una mejor <em>organización de catálogo</em>.'
      },
      {
        question: '¿Cómo cambio el precio de un producto?',
        answer: 'Edita el <em>precio</em> desde los detalles del producto. El cambio se guarda solo y no afecta <strong>órdenes en curso</strong> o finalizadas.'
      },
      {
        question: '¿Los productos soportan múltiples monedas?',
        answer: 'Sí, puedes mostrar tus <strong>productos</strong> en diferentes <em>monedas</em> según la configuración de la tienda y la <em>ubicación del cliente</em>.'
      }
    ]
  },
  en: {
    title: 'Products and inventory',
    description:
      'Manage your products and inventory with practical guides. Track stock, organize catalogs, optimize processes, and grow your business.',
    page_id: PagesId.ItemsHome,
    seo_keywords:
      'inventory, products, wicadu, inventory management, products and inventory, inventory software, product catalog organization, change product prices, inventory management for beginners, how to add products, how to sell more online',
    breadcrumbs: {
      home: 'Home',
      items_home: 'Products and inventory',
    },
    managing_items: 'Inventory management',
    customize_items: 'Product customization',
    faq: [
      {
        question: 'Can I control the stock of my products?',
        answer: 'Yes, each product has an <em>inventory</em> field where you can specify how many <strong>units are available</strong>, simplifying <em>inventory management</em>.'
      },
      {
        question: 'What happens when a product runs out of stock?',
        answer: 'The product is automatically marked as <strong>“Out of Stock”</strong> and becomes unavailable for sale. To restock, add more units via the <em>purchasing module</em>.'
      },
      {
        question: 'Can I import multiple products at once?',
        answer: 'For now, wicadu only supports customizing <em>products</em> individually with all their details, <strong>variants</strong>, and images quickly and easily.'
      },
      {
        question: 'Does wicadu support variants like sizes or colors?',
        answer: 'Yes, you can create <strong>variants</strong> such as <em>size</em>, <em>color</em>, or <em>material</em> within the same product for better <em>catalog organization</em>.'
      },
      {
        question: 'How do I change the price of a product?',
        answer: 'Edit the <em>price</em> from the product details. The change is saved automatically and does not affect <strong>ongoing or completed orders</strong>.'
      },
      {
        question: 'Do products support multiple currencies?',
        answer: 'Yes, you can display your <strong>products</strong> in different <em>currencies</em> based on the store\'s settings and the <em>custome\'s location</em>.'
      }
    ]
  },
  fr: {
    title: 'Produits et inventaire',
    description:
      "Gérez vos produits et votre inventaire grâce à des guides pratiques. Suivez le stock, organisez les catalogues, optimisez vos processus et développez votre activité.",
    page_id: PagesId.ItemsHome,
    seo_keywords:
      "inventaire, produits, wicadu, gestion des stocks, produits et inventaire, logiciel d'inventaire, organisation du catalogue produits, modifier les prix des produits, gestion des stocks pour débutants, comment ajouter des produits",
    breadcrumbs: {
      home: 'Accueil',
      items_home: 'Produits et inventaire',
    },
    managing_items: "Gestion de l'inventaire",
    customize_items: 'Personnalisation des produits',
    faq: [
      {
        question: 'Puis-je contrôler le stock de mes produits ?',
        answer:
          "Oui, chaque produit possède un champ <em>inventaire</em> dans lequel vous pouvez indiquer combien d'<strong>unités sont disponibles</strong>, ce qui simplifie la <em>gestion des stocks</em>."
      },
      {
        question: "Que se passe-t-il lorsqu'un produit est en rupture de stock ?",
        answer:
          'Le produit est automatiquement marqué comme <strong>« En rupture de stock »</strong> et devient indisponible à la vente. Pour le réapprovisionner, ajoutez davantage d’unités via le <em>module des achats</em>.'
      },
      {
        question: 'Puis-je importer plusieurs produits à la fois ?',
        answer:
          'Pour le moment, wicadu permet uniquement de personnaliser les <em>produits</em> individuellement avec tous leurs détails, <strong>variantes</strong> et images, rapidement et simplement.'
      },
      {
        question: 'wicadu prend-il en charge des variantes comme les tailles ou les couleurs ?',
        answer:
          'Oui, vous pouvez créer des <strong>variantes</strong> telles que la <em>taille</em>, la <em>couleur</em> ou le <em>matériau</em> au sein d’un même produit pour une meilleure <em>organisation du catalogue</em>.'
      },
      {
        question: "Comment modifier le prix d'un produit ?",
        answer:
          "Modifiez le <em>prix</em> depuis les détails du produit. Le changement est enregistré automatiquement et n'affecte pas les <strong>commandes en cours ou terminées</strong>."
      },
      {
        question: 'Les produits prennent-ils en charge plusieurs devises ?',
        answer:
          "Oui, vous pouvez afficher vos <strong>produits</strong> dans différentes <em>devises</em> selon les paramètres de la boutique et la <em>localisation du client</em>."
      }
    ]
  },
  pt: {},
}


export default globalTranslations
